import { SystemTypes } from 'spark-core-dx/services';

export const CLIENT_SECURITY_ITEM_NAMES = Object.freeze({
  authenticationWorkspace: 'ClientAuthenticationWorkspace',
  guestAccessWorkspace: 'ClientGuestAccessWorkspace',
  connectionForm: 'ClientEntraConnectionForm',
  azureMapsForm: 'ClientAzureMapsConnectionForm',
  mappingForm: 'ClientEntraMappingForm',
  rolesWorkspace: 'ClientRolesPermissionsWorkspace',
  roleForm: 'ClientRoleForm',
  permissionForm: 'ClientRolePermissionForm',
  roleMappingForm: 'ClientRoleMappingForm',
  profilesWorkspace: 'ClientUserProfilesWorkspace',
  profileForm: 'ClientUserProfileForm',
});

export const CLIENT_SECURITY_OPERATIONS = Object.freeze({
  loadAuthentication: 'LoadClientAuthentication',
  saveReadConnection: 'SaveClientEntraReadConnection',
  saveAzureMapsConnection: 'SaveClientAzureMapsConnection',
  removeAzureMapsOverride: 'RemoveClientAzureMapsOverride',
  validateReadConnection: 'ValidateClientEntraReadConnection',
  testLogin: 'TestClientEntraLogin',
  saveEntraMapping: 'SaveClientEntraMapping',
  deleteEntraMapping: 'DeleteClientEntraMapping',
  loadGuestAccess: 'LoadClientGuestAccess',
  saveGuestAccessMode: 'SaveClientGuestAccessMode',
  inviteGuest: 'InviteClientGuest',
  revokeGuest: 'RevokeClientGuest',
  reconcileGuestAccess: 'ReconcileClientGuestAccess',
  resumeGuestReconciliation: 'ResumeClientGuestReconciliation',
  loadRoles: 'LoadClientRolesPermissions',
  createRole: 'CreateClientRoleFromPermissionSet',
  cloneRole: 'CloneClientRole',
  saveRole: 'SaveClientRoleDetails',
  savePermission: 'SaveClientRolePermission',
  saveRoleMappings: 'SaveClientRoleMappings',
  setRoleLifecycle: 'SetClientRoleLifecycle',
  loadProfiles: 'LoadClientUserProfiles',
  searchEntraUsers: 'SearchClientEntraUsers',
  saveProfile: 'SaveClientUserProfile',
  assignRoles: 'AssignClientUserRoles',
  setUserAccess: 'SetClientUserAccess',
  repairEntraMapping: 'RepairClientUserEntraMapping',
});

export const CLIENT_ENTRA_CONNECTION_FIELDS = Object.freeze([
  'TenantId',
  'TenantDomain',
  'SignInClientId',
  'ReadClientId',
  'ReadClientSecretInput',
  'Domain',
  'ScopeAudience',
  'DisplayName',
  'Icon',
]);

export const CLIENT_ENTRA_PROVIDER_SETUP_FIELDS = Object.freeze([
  'DisplayName',
  'TenantId',
  'TenantDomain',
  'SignInClientId',
  'ReadClientId',
  'ReadClientSecretInput',
]);

export const CLIENT_ENTRA_MAPPING_FIELDS = Object.freeze([
  'DisplayName',
  'TenantId',
  'AuthProviderClientId',
  'ScopeIdUri',
  'Icon',
  'Enabled',
]);

export const CLIENT_AZURE_MAPS_CONNECTION_FIELDS = Object.freeze([
  'Enabled',
  'AuthenticationMode',
  'AccountClientId',
  'TenantId',
  'ApplicationClientId',
  'ClientSecretInput',
]);

export const CLIENT_ROLE_FIELDS = Object.freeze([
  'PermissionSetId',
  'RoleName',
  'RoleCode',
  'Description',
  'Icon',
  'Precedence',
  'RedirectScreenKey',
  'Activate',
]);

export const CLIENT_ROLE_EDIT_FIELDS = Object.freeze([
  'RoleName',
  'Description',
  'Icon',
  'Precedence',
  'RedirectScreenKey',
]);

export const CLIENT_PERMISSION_FIELDS = Object.freeze([
  'Permission',
  'CanView',
  'CanCreate',
  'CanEdit',
  'CanDelete',
  'CanHardDelete',
  'CanExecute',
  'CanAccess',
  'CanViewOnNav',
]);

export const CLIENT_ROLE_MAPPING_FIELDS = Object.freeze([
  'GroupIdentifiers',
  'ScopeValues',
]);

export const NO_PERMISSION_SET_VALUE = 'NoPermissionSet';
export const MINIMUM_CLIENT_ROLE_PRECEDENCE = 100;
export const DEFAULT_CLIENT_ROLE_PRECEDENCE = 999;
export const DEFAULT_CLIENT_PROFILE_COUNTRY = 'US';
export const DEFAULT_CLIENT_PROFILE_LOCALE = 'en-US';

export const CLIENT_PROFILE_LOCALE_OPTIONS = Object.freeze([
  { Text: 'English (United States)', Value: 'en-US' },
  { Text: 'English (Canada)', Value: 'en-CA' },
  { Text: 'English (United Kingdom)', Value: 'en-GB' },
  { Text: 'French (Canada)', Value: 'fr-CA' },
  { Text: 'French (France)', Value: 'fr-FR' },
  { Text: 'Spanish (United States)', Value: 'es-US' },
  { Text: 'Spanish (Mexico)', Value: 'es-MX' },
  { Text: 'Spanish (Spain)', Value: 'es-ES' },
  { Text: 'German (Germany)', Value: 'de-DE' },
]);

export const normalizeClientProfileAddress = (
  address,
  { defaultCountry = false } = {},
) => {
  const source = address || {};
  const {
    Address1: legacyStreetOne,
    Address2: legacyStreetTwo,
    Region: legacyState,
    ...remaining
  } = source;

  return {
    ...remaining,
    StreetOne: source.StreetOne ?? legacyStreetOne ?? '',
    StreetTwo: source.StreetTwo ?? legacyStreetTwo ?? '',
    City: source.City ?? '',
    State: source.State ?? legacyState ?? '',
    ZipCode: source.ZipCode ?? '',
    County: source.County ?? '',
    Country: source.Country
      || (defaultCountry ? DEFAULT_CLIENT_PROFILE_COUNTRY : ''),
  };
};

export const mergeClientProfileDraftChange = (
  currentDraft,
  changedFieldName,
  nextDraft,
) => {
  const current = currentDraft || {};
  const incoming = nextDraft || {};
  const merged = {
    ...current,
    ...incoming,
  };

  if (changedFieldName === 'Address') {
    merged.Address = normalizeClientProfileAddress({
      ...(current.Address || {}),
      ...(incoming.Address || {}),
    });
  } else if (current.Address !== undefined) {
    // AddressControl owns the structured address update. Other controls can
    // emit a stale form snapshot, so a timezone/locale change must not replace
    // the canonical address already held by this screen.
    merged.Address = normalizeClientProfileAddress(current.Address);
  } else if (incoming.Address !== undefined) {
    merged.Address = normalizeClientProfileAddress(incoming.Address);
  }

  return merged;
};

export const CLIENT_PROFILE_FIELDS = Object.freeze([
  'FirstName',
  'MiddleName',
  'LastName',
  'DisplayName',
  'Email',
  'SecondEmail',
  'PrimaryPhone',
  'MobilePhone',
  'Address',
  'TimeZone',
  'Locale',
  'Organization',
  'Title',
  'Division',
  'Department',
  'EmployeeNumber',
  'RoleCodes',
]);

export const CLIENT_ROLE_ASSIGNMENT_FIELD = Object.freeze({
  FieldName: 'RequestedRoleCodes',
  DisplayName: 'Roles',
  Description: 'Only existing active client roles are available.',
  FieldType: SystemTypes.UIFieldType.MultiSelectList,
  Format: 'string-list',
});

const label = (fieldName, displayName, sequence, presentation = {}) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: 'Label',
  ShowOnGrid: true,
  GridSequence: sequence,
  CanFilter: true,
  CanSort: true,
  ...presentation,
});

export const ACTIVE_AUTH_PROVIDER_FIELD = Object.freeze({
  FieldName: 'ProviderMappingId',
  DisplayName: 'Authentication Provider',
  Description: 'Choose an active authentication provider configuration.',
  FieldType: SystemTypes.UIFieldType.SelectList,
  IsRequired: true,
});

export const AUTH_MAPPING_CARD_METADATA = Object.freeze({
  Name: 'ClientEntraMappingSummary',
  DisplayName: 'Provider Mappings',
  GridType: 'Card',
  Fields: [
    label('DisplayName', 'Provider', 1),
    label('Status', 'Status', 2),
    label('TenantId', 'Tenant ID', 3),
    label('AuthProviderClientId', 'App Client ID', 4),
  ],
});

export const AUTH_PROVIDER_SELECTION_CARD_METADATA = Object.freeze({
  Name: 'ClientAuthenticationProviderOption',
  DisplayName: 'Authentication Providers',
  GridType: 'Card',
  Fields: [
    label('DisplayName', 'Authentication Provider', 1),
    label('Availability', 'Availability', 2),
  ],
});

export const AUTH_PROVIDER_SELECTION_OPTIONS = Object.freeze([
  Object.freeze({
    Key: 'EntraID',
    DisplayName: 'Microsoft Entra ID',
    Availability: 'Available',
    Description: 'Add a Microsoft Entra provider mapping for this client application.',
    IsSelectable: true,
  }),
  Object.freeze({
    Key: 'Guest',
    DisplayName: 'Guest / External Access',
    Availability: 'Available when environment-ready',
    Description: 'Use the Convoke-hosted External Access tenant with explicit Guest invitations and application membership.',
    IsSelectable: true,
  }),
  Object.freeze({
    Key: 'AuthGuard',
    DisplayName: 'Convoke AuthGuard',
    Availability: 'Not available',
    Description: 'AuthGuard provider mappings are not available in Client Command.',
    IsSelectable: false,
  }),
]);

export const GUEST_ACCESS_CONFIGURATION_FIELDS = Object.freeze([
  Object.freeze({
    FieldName: 'ReadinessStatus',
    DisplayName: 'Readiness',
    Description: 'Calculated by the server from the External Access environment, application domain, callback, and purpose-scoped identities.',
    FieldType: SystemTypes.UIFieldType.Label,
    IsRequired: false,
  }),
  Object.freeze({
    FieldName: 'CanonicalOrigin',
    DisplayName: 'Canonical application origin',
    Description: 'Exact active HTTPS application origin for Guest login. Local development may use the approved localhost origin.',
    FieldType: SystemTypes.UIFieldType.TextBox,
    IsRequired: true,
  }),
  Object.freeze({
    FieldName: 'RedirectUri',
    DisplayName: 'Verified callback URI',
    Description: 'Exact same-origin /microsoft/sso callback already verified by the External Access deployment reconciler.',
    FieldType: SystemTypes.UIFieldType.TextBox,
    IsRequired: true,
  }),
  Object.freeze({
    FieldName: 'GuestAccessActive',
    DisplayName: 'Active',
    Description: 'Allow Guest sign-in for this application. Activation is available only when Readiness is Ready.',
    FieldType: SystemTypes.UIFieldType.CheckBox,
    IsRequired: false,
  }),
]);

export const GUEST_PROFILE_CANDIDATE_CARD_METADATA = Object.freeze({
  Name: 'ClientGuestProfileCandidateSummary',
  DisplayName: 'Profiles available to invite',
  GridType: 'Card',
  Fields: [
    label('DisplayName', 'Profile', 1),
    label('Email', 'Email', 2),
    label('AssignedRoles', 'Assigned roles', 3),
  ],
});

export const GUEST_INVITATION_CARD_METADATA = Object.freeze({
  Name: 'ClientGuestInvitationSummary',
  DisplayName: 'Guest invitations',
  GridType: 'Card',
  Fields: [
    label('DisplayName', 'Guest', 1),
    label('Email', 'Email', 2),
    label('ProfileName', 'Profile', 3),
    label('AssignedRoles', 'Assigned roles', 4),
    label('DeliveryStatus', 'Delivery', 5),
    label('NextStep', 'Next step', 6),
  ],
});

export const CLIENT_ROLE_GRID_METADATA = Object.freeze({
  Name: 'ClientRoleSummary',
  DisplayName: 'Client Roles',
  GridType: 'Standard',
  Fields: [
    label('RoleName', 'Role Name', 1),
    label('RoleCode', 'Role Code', 2),
    label('Status', 'Status', 3),
    label('Precedence', 'Precedence', 4),
    label('SourcePermissionSet', 'Source Permission Set', 5),
    label('SourceVersion', 'Source Version', 6),
    label('LocalChangeCount', 'Local Changes', 7),
    label('AssignedUserCount', 'Assigned Users', 8),
    label('MappingCount', 'Mappings', 9),
  ],
});

export const CLIENT_PERMISSION_GRID_METADATA = Object.freeze({
  Name: 'ClientRolePermissionSummary',
  DisplayName: 'Role Permissions',
  GridType: 'Standard',
  Fields: [
    label('Permission', 'Permission', 1),
    label('Baseline', 'Recorded Baseline', 2),
    label('Stored', 'Stored Client Setting', 3),
    label('Effective', 'Effective', 4),
    label('Modified', 'Modified', 5),
    label('Dependencies', 'Dependencies', 6),
    label('Blocker', 'Blocker', 7),
  ],
});

export const CLIENT_ASSIGNED_USERS_GRID_METADATA = Object.freeze({
  Name: 'ClientRoleAssignedUserSummary',
  DisplayName: 'Assigned Users',
  GridType: 'Standard',
  Fields: [
    label('Name', 'Name', 1),
    label('Email', 'Email / UPN', 2),
    label('Status', 'Profile Status', 3),
    label('Roles', 'Assigned Roles', 4),
  ],
});

export const CLIENT_PROFILE_GRID_METADATA = Object.freeze({
  Name: 'ClientUserProfileSummary',
  DisplayName: 'User Profiles',
  GridType: 'Standard',
  GridStyles: [],
  GridDensity: 'Medium',
  HasPaging: [],
  ServerFilterSort: false,
  Fields: [
    label('Name', 'Name', 1, { ColumnWidth: 10, MinColumnWidth: 8 }),
    label('Email', 'Email / User Principal Name', 2, { ColumnWidth: 18, MinColumnWidth: 12 }),
    label('EntraAccountStatus', 'Entra Account Status', 3, { ColumnWidth: 11, MinColumnWidth: 9 }),
    label('ConvokeProfileStatus', 'Convoke Profile Status', 4, { ColumnWidth: 11, MinColumnWidth: 9 }),
    label('AssignedRoles', 'Assigned Roles', 5, { ColumnWidth: 12, MinColumnWidth: 9 }),
    label('EntraMappingStatus', 'Entra Mapping Status', 6, { ColumnWidth: 11, MinColumnWidth: 9 }),
    label('ReconciliationStatus', 'Reconciliation Status', 7, { ColumnWidth: 11, MinColumnWidth: 9 }),
  ],
});

export const DIRECTORY_USER_CARD_METADATA = Object.freeze({
  Name: 'ClientDirectoryUserSearchResult',
  DisplayName: 'Users',
  GridType: 'Card',
  Fields: [
    label('DisplayName', 'Name', 1),
    label('Email', 'Email', 2),
  ],
});

export const cloneSecurityValue = (value) => (
  value == null ? value : JSON.parse(JSON.stringify(value))
);

export const selectSecurityFields = (metadata, fieldNames) => {
  const available = new Map(
    (metadata?.Fields || metadata?.fields || []).map((field) => ([
      String(field?.FieldName || field?.fieldName || '').toLowerCase(),
      field,
    ])),
  );
  return fieldNames
    .map((name) => available.get(String(name).toLowerCase()))
    .filter(Boolean);
};

export const createSecurityRequest = ({
  workspace,
  clientId,
  appId,
  currentEnvironment,
  overrides = {},
}) => ({
  ...(workspace || {}),
  ClientId: clientId,
  ApplicationId: appId,
  CurrentEnvironment: currentEnvironment,
  LoginToken: '',
  ...overrides,
});

export const permissionSetFormFields = (fields, permissionSets) => (
  fields.map((field) => {
    if (String(field?.FieldName).toLowerCase() !== 'permissionsetid') return field;
    return {
      ...field,
      FieldType: SystemTypes.UIFieldType.SelectList,
      DataJson: JSON.stringify([
        {
          Text: 'Create Empty Role',
          Value: NO_PERMISSION_SET_VALUE,
          SortOrder: 0,
        },
        ...(permissionSets || []).map((permissionSet, index) => ({
          Text: `${permissionSet.Name} (v${permissionSet.Version})`,
          Value: permissionSet.Id,
          SortOrder: index + 1,
        })),
      ]),
    };
  })
);

export const permissionSetRoleDefaults = (permissionSet) => {
  const roleName = String(permissionSet?.Name ?? '').trim();
  return {
    RoleName: roleName,
    RoleCode: roleName.replace(/[^a-zA-Z0-9]/g, ''),
    Description: String(permissionSet?.Description ?? '').trim(),
  };
};

export const profileRoleFormFields = (fields, roles) => (
  fields.map((field) => {
    const fieldName = String(field?.FieldName).toLowerCase();
    if (fieldName === 'rolecodes') {
      return {
        ...field,
        FieldType: SystemTypes.UIFieldType.MultiSelectList,
        DataJson: JSON.stringify((roles || []).map((role) => ({
          Text: role.DisplayName,
          Value: role.RoleCode,
        }))),
      };
    }
    if (fieldName === 'locale') {
      return {
        ...field,
        FieldType: SystemTypes.UIFieldType.SelectList,
        DefaultSelection: DEFAULT_CLIENT_PROFILE_LOCALE,
        DataJson: JSON.stringify(CLIENT_PROFILE_LOCALE_OPTIONS),
      };
    }
    return field;
  })
);

export const roleOptionsData = (roles) => JSON.stringify(
  (roles || []).map((role) => ({
    Text: role.DisplayName,
    Value: role.RoleCode,
  })),
);
